import React, { useEffect, useState } from "react";

import { Box, Snackbar, Alert } from "@mui/material";

import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import EmployeeHeader from "./components/EmployeeHeader";
import EmployeeStatistics from "./components/EmployeeStatistics";
import EmployeeFilters from "./components/EmployeeFilters";
import EmployeeGrid from "./components/EmployeeGrid";
import EditEmployeeModal from "./components/EditEmployeeModal";

import {
  getEmployees,
  getDepartments,
  createEmployee,
  updateEmployee,
} from "./api/employeeService";

const EmployeeManagement = () => {
  // ==========================
  // REDUX
  // ==========================
  const { t } = useTranslation("UserManagement");
  const userState = useSelector((state) => state.user);
  const isFrench = userState.lang === "FR";
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const token = userState?.token;

  const noEmplacement = userState?.location?.noEmplacement;

  // ==========================
  // STATES
  // ==========================

  const [employees, setEmployees] = useState([]);

  const [departments, setDepartments] = useState([]);

  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    search: "",

    department: "",

    status: "",
  });

  const [openModal, setOpenModal] = useState(false);

  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const showMessage = (messageFr, messageEn, severity = "success") => {
    setSnackbar({
      open: true,
      message: isFrench ? messageFr : messageEn,
      severity,
    });
  };
  // ==========================
  // LOAD EMPLOYEES
  // ==========================

  const loadEmployees = async () => {
    if (!noEmplacement || !token) return;

    try {
      setLoading(true);

      const data = await getEmployees(
        noEmplacement,

        token,
      );

      let index = 1;

      const rows = (data || []).map((e) => ({
        id: index++,
        noEmploye: e.noEmploye,
        isInactive: e.estInactif,

        firstName: e.firstname,

        lastName: e.lastName,

        username: e.username,

        email: e.email,

        nodooliz: e.posPwd,

        langue: e.language,

        department: e.department?.textEn || "",

        departmentId: e.idDepartement,

        lastLogin: e.lastLogin,

        estOperateur: e.estOperateur,
      }));

      setEmployees(rows);
    } catch (error) {
      console.error("Load employees error", error);
    } finally {
      setLoading(false);
    }
  };

  // ==========================
  // LOAD DEPARTMENTS
  // ==========================

  const loadDepartments = async () => {
    if (!token) return;

    try {
      const data = await getDepartments(token);

      setDepartments(data || []);
    } catch (error) {
      console.error("Load departments error", error);
    }
  };

  // ==========================
  // INITIAL LOAD
  // ==========================

  useEffect(() => {
    if (noEmplacement && token) {
      loadEmployees();

      loadDepartments();
    }
  }, [noEmplacement, token]);

  // ==========================
  // REFRESH
  // ==========================

  const handleRefresh = () => {
    loadEmployees();
  };

  // ==========================
  // ADD
  // ==========================

  const handleAddEmployee = () => {
    setSelectedEmployee(null);

    setOpenModal(true);
  };

  // ==========================
  // EDIT
  // ==========================

  const handleEditEmployee = (employee) => {
    setSelectedEmployee(employee);

    setOpenModal(true);
  };

  // ==========================
  // SAVE
  // ==========================

  const handleSaveEmployee = async (employee) => {
    try {
      setLoading(true);

      const employeeDto = {
        firstName: employee.firstName,

        lastName: employee.lastName,

        username: employee.username,

        email: employee.email,

        // IMPORTANT
        noDooliz: employee.nodooliz,

        language: employee.langue,

        departmentId: employee.departmentId,

        estOperateur: employee.estOperateur,

        estInactif: employee.isInactive,

        // vient de Redux
        noEmplacement: noEmplacement,
      };

      let response;

      if (selectedEmployee) {
        // =====================
        // UPDATE
        // =====================
        employeeDto.noEmploye = selectedEmployee.noEmploye;

        response = await updateEmployee(employeeDto, token);
      } else {
        // =====================
        // CREATE
        // =====================

        response = await createEmployee(employeeDto, token);
      }

      if (response?.success) {
        showMessage(response.messageFr, response.messageEn, "success");

        setOpenModal(false);

        await loadEmployees();
      }
    } catch (error) {
      console.error("Save employee error", error);

      // erreur venant du ApiResponse
      if (error?.messageFr) {
        showMessage(error.messageFr, error.messageEn, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  // ==========================
  // DELETE
  // ==========================

  const handleDeleteEmployee = (employee) => {
    console.log("Delete employee", employee);
  };

  // ==========================
  // RESET FILTERS
  // ==========================

  const resetFilters = () => {
    setFilters({
      search: "",

      department: "",

      status: "",
    });
  };

  // ==========================
  // FILTER EMPLOYEES
  // ==========================

  const filteredEmployees = (employees || []).filter((employee) => {
    const search = filters.search?.toLowerCase() || "";

    const fullName = `${employee.firstName || ""}
        ${employee.lastName || ""}`.toLowerCase();

    const matchSearch =
      !search ||
      fullName.includes(search) ||
      employee.username?.toLowerCase().includes(search) ||
      employee.email?.toLowerCase().includes(search);

    const matchDepartment =
      !filters.department || employee.department === filters.department;

    const matchStatus =
      !filters.status ||
      (filters.status === "Active" && !employee.isInactive) ||
      (filters.status === "Inactive" && employee.isInactive);

    return matchSearch && matchDepartment && matchStatus;
  });

  return (
    <Box
      sx={{
        p: 3,
      }}
    >
      <EmployeeHeader
        onAddEmployee={handleAddEmployee}
        onRefresh={handleRefresh}
        loading={loading}
      />

      <EmployeeStatistics rows={employees} />

      <EmployeeFilters
        filters={filters}
        setFilters={setFilters}
        departments={departments}
        onReset={resetFilters}
      />

      <EmployeeGrid
        employees={filteredEmployees}
        loading={loading}
        onEdit={handleEditEmployee}
        onDelete={handleDeleteEmployee}
      />

      <EditEmployeeModal
        open={openModal}
        employee={selectedEmployee}
        departments={departments}
        token={token}
        onClose={() => setOpenModal(false)}
        onSave={handleSaveEmployee}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() =>
          setSnackbar((prev) => ({
            ...prev,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() =>
            setSnackbar((prev) => ({
              ...prev,
              open: false,
            }))
          }
          sx={{
            width: "100%",
            borderRadius: 2,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmployeeManagement;
